import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/store";
import TabMenu from "~/common/components/TabMenu";
import Empty from "../Empty";
import "~/assets/sass/pages/dashboard/dashboard-company.scss";
import DarkNoImg from "~/assets/img/defaultImg/dark-default-rec.png";
import LightNoImg from "~/assets/img/defaultImg/light-default-rec.png";
import { Case, If } from "~/common/components/If";
import useAlert from "~/common/hooks/useAlert";
import { INVEST_LEVEL } from "~/constant/ApplyFormConstant";
import { thousandFormatter } from "~/utils/formatter";
import { useTranslation } from "react-i18next";

const Company = ({ dashboardData }) => {
	const { t } = useTranslation();
	const [
		{
			sidebar: { sideBarList },
			theme: { theme },
		},
	] = useGlobalStore();
	const navigate = useNavigate();
	const [showViewAuthAlert, ViewAuthAlert] = useAlert();
	const [currentTab, setCurrentTab] = useState("new");
	const tabMenuList = [
		{ id: "new", title: t("pages.DashboardPage.Company.tabMenuList.title1") },
		{ id: "favorite", title: t("pages.DashboardPage.Company.tabMenuList.title2") },
		{ id: "recent", title: t("pages.DashboardPage.Company.tabMenuList.title3") },
	];

	const newStartups = dashboardData.newStartups;
	const favoriteStartups = dashboardData.favoriteStartups;
	const recentViewStartups = dashboardData.recentViewStartups;

	const handleAuthClick = (newItem, favoriteItem, recentItem) => {
		const checkAuth = sideBarList?.main[2].subMenu[0].auth;
		let selectedIdx;
		if (checkAuth === "view") {
			navigate(`/invest/list`);
		} else if (checkAuth === "none") {
			showViewAuthAlert();
			return;
		} else {
			selectedIdx = newItem
				? newItem.idx
				: favoriteItem
				? favoriteItem.idx
				: recentItem
				? recentItem.idx
				: null;
			navigate(`/invest/list?modalIdx=${selectedIdx}`);
		}
	};

	const getCompanyInfo = (item) => {
		const addressPrefix = item?.address ? item.address.substring(0, 2) : "-";
		const companyAge = new Date().getFullYear() - parseInt(item.estDate?.substring(0, 4)) + 1;
		const investmentRound =
			INVEST_LEVEL.find((value) => value.value === item?.CompanyInvestInfo?.round)?.label ||
			"-";
		let investmentTarget = "-";

		if (item.CompanyInvestInfo.target) {
			if (item.CompanyInvestInfo.target > 10000000) {
				investmentTarget =
					(item.CompanyInvestInfo.target / 100000000).toFixed(1) +
					t("pages.DashboardPage.Company.unit.hMillion");
			} else if (item.CompanyInvestInfo.target > 1000000) {
				investmentTarget =
					item.CompanyInvestInfo.target.substring(0, 1) +
					t("pages.DashboardPage.Company.unit.million");
			} else {
				investmentTarget =
					thousandFormatter(item.CompanyInvestInfo.target) +
					t("pages.DashboardPage.Company.unit.won");
			}
		}

		return `${addressPrefix} / ${companyAge}${t(
			"pages.DashboardPage.Company.unit.year"
		)} / ${investmentRound} / seed (${investmentTarget})`;
	};

	return (
		<article className="item company">
			<TabMenu tabItem={tabMenuList} currentTab={currentTab} setCurrentTab={setCurrentTab} />
			<If condition={currentTab}>
				<Case condition="new">
					<div className="c__table">
						<div className="thead">
							<div className="tr">
								<div className="th name">
									{t("pages.DashboardPage.Company.table.th1")}
								</div>
								<div className="th info">
									{t("pages.DashboardPage.Company.table.th2")}
								</div>
								<div className="th tag">
									{t("pages.DashboardPage.Company.table.th3")}
								</div>
							</div>
						</div>
						<div className="tbody">
							{newStartups?.map((newItem, index) => (
								<div className="tr" key={index}>
									<div className="td name">
										<div className="img-wrap">
											<img
												src={
													newItem.logoSqu ||
													(theme === "dark" ? DarkNoImg : LightNoImg)
												}
												alt={t("pages.DashboardPage.Company.table.alt")}
											/>
										</div>
										<div
											className="name-wrap"
											onClick={() => {
												handleAuthClick(newItem);
											}}
										>
											{newItem.name}
										</div>
									</div>
									<div className="td info">{getCompanyInfo(newItem)}</div>
									<div className="td tag">
										{newItem.CompanyProduct[0].CompanyProductKeyword.map(
											(tag) => `#${tag.content} `
										)}
									</div>
								</div>
							))}
						</div>
					</div>
				</Case>
				<Case condition="favorite">
					{favoriteStartups?.length > 0 ? (
						<div className="c__table">
							<div className="thead">
								<div className="tr">
									<div className="th name">
										{t("pages.DashboardPage.Company.table.th1")}
									</div>
									<div className="th info">
										{t("pages.DashboardPage.Company.table.th2")}
									</div>
									<div className="th tag">
										{t("pages.DashboardPage.Company.table.th3")}
									</div>
								</div>
							</div>
							<div className="tbody">
								{favoriteStartups?.map((favoriteItem, index) => (
									<div className="tr" key={index}>
										<div className="td name">
											<div className="img-wrap">
												<img
													src={
														favoriteItem.logoSqu ||
														(theme === "dark" ? DarkNoImg : LightNoImg)
													}
													alt={t("pages.DashboardPage.Company.table.alt")}
												/>
											</div>
											<div
												className="name-wrap"
												onClick={() => {
													handleAuthClick(favoriteItem);
												}}
											>
												{favoriteItem.name}
											</div>
										</div>
										<div className="td info">
											{getCompanyInfo(favoriteItem)}
										</div>
										<div className="td tag">
											{favoriteItem.CompanyProduct[0].CompanyProductKeyword.map(
												(tag) => `#${tag.content} `
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					) : (
						<Empty
							message={t("pages.DashboardPage.Company.Empty1")}
							button={t("pages.DashboardPage.Company.add")}
							handleAdd={() => navigate("/invest/list")}
						/>
					)}
				</Case>
				<Case condition="recent">
					{recentViewStartups?.length > 0 ? (
						<div className="c__table">
							<div className="thead">
								<div className="tr">
									<div className="th name">
										{t("pages.DashboardPage.Company.table.th1")}
									</div>
									<div className="th info">
										{t("pages.DashboardPage.Company.table.th2")}
									</div>
									<div className="th tag">
										{t("pages.DashboardPage.Company.table.th3")}
									</div>
								</div>
							</div>
							<div className="tbody">
								{recentViewStartups?.map((recentItem, index) => (
									<div className="tr" key={index}>
										<div className="td name">
											<div className="img-wrap">
												<img
													src={
														recentItem.logoSqu ||
														(theme === "dark" ? DarkNoImg : LightNoImg)
													}
													alt={t("pages.DashboardPage.Company.table.alt")}
												/>
											</div>
											<div
												className="name-wrap"
												onClick={() => {
													handleAuthClick(recentItem);
												}}
											>
												{recentItem.name}
											</div>
										</div>
										<div className="td info">{getCompanyInfo(recentItem)}</div>
										<div className="td tag">
											{recentItem.CompanyProduct[0].CompanyProductKeyword.map(
												(tag) => `#${tag.content} `
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					) : (
						<Empty
							message={t("pages.DashboardPage.Company.Empty2")}
							button={t("pages.DashboardPage.Company.check")}
							handleAdd={() => navigate("/invest/list")}
						/>
					)}
				</Case>
			</If>
			<ViewAuthAlert
				message={t("pages.DashboardPage.Company.ViewAuthAlert")}
				type="warning"
				confirm={t("common.commonWord.button.check")}
			/>
		</article>
	);
};

export default Company;
